import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CertificateType, PERMISSION } from '@lib/core/enum';
import { QueryParams } from '@proget-shared';
import { GridAction, GridActionPrompt, GridActionType, GridActionsModule } from '@proget-shared/grid/grid-actions';
import { GridControl, GridControlModule, GridControlService, GridFilterType, PagingList } from '@proget-shared/grid/grid-control';
import { SelectionService } from '@proget-shared/grid/selection';
import { TableModule } from '@proget-shared/grid/table';
import { TableActionsModule } from '@proget-shared/grid/table-actions';
import { QueryParamsService } from '@proget-shared/query-params';
import { TranslateModule } from '@proget-shared/translate';
import { EllipsisModule } from '@proget-shared/ui/ellipsis';
import { LinkModule } from '@proget-shared/ui/link';
import { TableCellComponent } from '@proget-shared/ui/table-cell';
import { combineLatest, Observable, startWith, Subject } from 'rxjs';

import { UserCertificateStatus } from '../../../enum/user-certificate-status.enum';
import { User } from '../../../model';
import { UserCertificate } from '../../../model/user-certificate';
import { UserCardService } from '../../../service/user-card.service';
import { UserCardRepository } from '../../../user-card.repository';

@Component({
  selector: 'lib-certificates',
  templateUrl: './certificates.component.html',
  styleUrl: './certificates.component.scss',
  imports: [
    CommonModule,
    GridControlModule,
    GridActionsModule,
    TableModule,
    TableActionsModule,
    TableCellComponent,
    EllipsisModule,
    LinkModule,
    TranslateModule,
  ],
  providers: [GridControlService, QueryParamsService, SelectionService],
})
export class CertificatesComponent {
  protected readonly gridActions: GridAction<UserCertificate>[] = [
    {
      label: 'button.renew.label',
      icon: 'icon-repeat-icon',
      permission: PERMISSION.USER_CARD_ALLOW_MANAGE,
      prompt: {
        type: GridActionPrompt.CONFIRM,
        header: 'button.renew.label',
        message: 'confirm_action.message',
      },
      type: GridActionType.BATCH_WORK,
      batchWorkConfiguration: {
        actionKey: 'button.renew.label',
      },
      enabled: ({ revoked, profileCertificate }) => !revoked && !!profileCertificate,
      action: (id): Observable<void> => this.repository.renewUserCertificate(id),
      success: {
        callback: (): void => {
          this.reloadList();
        },
      },
    },
    {
      label: 'button.revoke.label',
      icon: 'icon-cleaning-icon',
      permission: PERMISSION.USER_CARD_ALLOW_MANAGE,
      prompt: {
        type: GridActionPrompt.PHRASE,
        header: 'button.revoke.label',
        message: 'confirm_action.message',
        phrase: 'Revoke',
      },
      type: GridActionType.BATCH_WORK,
      batchWorkConfiguration: {
        actionKey: 'button.revoke.label',
      },
      enabled: ({ revoked, profileCertificateType }): boolean => !revoked && profileCertificateType === CertificateType.TEMPLATE,
      action: (id): Observable<void> => this.repository.revokeUserCertificate(id),
      success: {
        callback: (): void => {
          this.reloadList();
        },
      },
    },
  ];
  protected readonly gridControl: GridControl;

  protected pagingList: PagingList<UserCertificate> | undefined;

  protected readonly selectionService = inject(SelectionService);

  private readonly destroyRef = inject(DestroyRef);
  private readonly repository = inject(UserCardRepository);
  private readonly reloadSubject = new Subject<void>();

  constructor() {
    const userCardService = inject(UserCardService);
    const gridControlService = inject(GridControlService);

    this.gridControl = gridControlService.registerGridControl('userCertificates');

    combineLatest([
      userCardService.user$,
      this.gridControl.requestParams$,
      this.reloadSubject.pipe(startWith(null)),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ([user, params]) => {
          this.loadList(user, params);
        },
      });

    this.gridControl.setFiltersConfig([
      {
        name: 'profileName',
        label: 'common.profile',
        placeholder: 'text_input.filter_placeholder',
        type: GridFilterType.INPUT,
        defaultInlineVisibility: true,
      },
      {
        name: 'issuer',
        label: 'settings.certificate.issuer',
        placeholder: 'text_input.filter_placeholder',
        type: GridFilterType.INPUT,
        defaultInlineVisibility: true,
      },
      {
        name: 'validity',
        label: 'common.validity',
        placeholder: 'date.placeholder_shorten',
        type: GridFilterType.RANGE_DATE,
        requestAlias: {
          from: 'validityFrom',
          to: 'validityUntil',
        },
      },
      {
        name: 'status',
        label: 'settings.certificate.revoked',
        placeholder: 'select.placeholder',
        type: GridFilterType.SELECT,
        options: [
          {
            label: 'common.yes',
            value: UserCertificateStatus.REVOKED,
          },
          {
            label: 'common.no',
            value: UserCertificateStatus.CREATED,
          },
        ],
        defaultInlineVisibility: true,
      },
    ]);

    this.destroyRef.onDestroy(() => {
      this.reloadSubject.complete();
    });
  }

  private loadList(user: User, params: QueryParams): void {
    this.repository
      .getAllUserCertificates(user.uuid, params)
      .subscribe({
        next: (certificatesList) => {
          this.pagingList = certificatesList;
          this.gridControl.setItemsCount(certificatesList.total);
          this.selectionService.setVisibleItems(certificatesList.items);
        },
        error: () => {
          this.pagingList = new PagingList();
          this.gridControl.setItemsCount(0);
          this.selectionService.setVisibleItems([]);
        },
      });
  }

  private reloadList(): void {
    this.reloadSubject.next();
  }
}
